import { useEffect } from 'react'
import { useMutation } from 'react-query'
import {
  useCreateScheduledPostTarget,
  createScheduledPostTarget,
} from 'hooks/queries/scheduledPostTargets/useCreateScheduledPostTarget'
import { useDeleteScheduledPostTarget } from 'hooks/queries/scheduledPostTargets/useDeleteScheduledPostTarget'
import { usePublishScheduledPostTargets } from 'hooks/queries/scheduledPostTargets/usePublishScheduledPostTargets'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostTarget } from 'types'
import Styles from './ScheduledPostTargetsPreview.module.scss'
import { Settings } from 'blaze-ui/icons'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ScheduledPostTargetPreviewItem } from './ScheduledPostTargetPreviewItem'
import { ManageScheduledPostTargetsModal } from 'components/ManageScheduledPostTargetsModal/ManageScheduledPostTargetsModal'
import { useModals } from 'almanac-ui/Modal'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { validateScheduledPost } from 'utils/validateScheduledPost'
import { ToolbarMenu } from 'screens/Calendar/FullScreenPreview/Toolbar/ToolbarMenu'
import { Button } from 'blaze-ui/Button'
import { CalendarFilters } from 'screens/Calendar/types'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { uniqBy } from 'lodash'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { isStoryChannel } from 'screens/Calendar/FullScreenPreview/utils'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

interface ScheduledPostTargetsPreviewProps {
  almanacFile: AlmanacFileShowItem
  activePlatform?: ExternalIntegrationEnum
  onSelectPlatform: (platform: ExternalIntegrationEnum) => void
  status: PostStatusPropertyValue
  calendarItemKey?: string
  filters?: CalendarFilters
  showVideoCrossPost?: boolean
  crossPostGroup: readonly ExternalIntegrationEnum[]
  onUpdateAlmanacFile?: (updatedFile: AlmanacFileShowItem) => void
  inDesigner?: boolean
}

export const SOCIAL_POST_PLATFORMS = [
  ExternalIntegrationEnum.Instagram,
  ExternalIntegrationEnum.Facebook,
  ExternalIntegrationEnum.LinkedIn,
  ExternalIntegrationEnum.X,
  ExternalIntegrationEnum.GoogleBusinessProfile,
] as const

export const ScheduledPostTargetsPreview: React.FC<ScheduledPostTargetsPreviewProps> = ({
  almanacFile,
  activePlatform = ExternalIntegrationEnum.X,
  onSelectPlatform,
  status,
  calendarItemKey,
  filters = {},
  showVideoCrossPost = false,
  crossPostGroup,
  onUpdateAlmanacFile,
  inDesigner = false,
}) => {
  const scheduledPostId = almanacFile.scheduled_post?.id as number
  const workspace = useCurrentWorkspace()
  const workspaceId = useCurrentWorkspaceId()
  const targets = almanacFile.scheduled_post_targets || []
  const scheduledPost = almanacFile.scheduled_post
  const createMutation = useCreateScheduledPostTarget()
  const deleteMutation = useDeleteScheduledPostTarget()
  const { mutate: publishScheduledPostTargets } = usePublishScheduledPostTargets()
  const { data: externalIntegrations } = useExternalIntegrations()
  const { openModal } = useModals()
  const availablePlatforms = externalIntegrations?.external_integrations.map((integration) => integration.provider)
  const { disconnectedIntegrations } = useIntegrationHeartbeatStatus()
  const approvalsEnabled = useFlipperFlag('post_approvals')
  const isSystemRedesignEnabled = useIsSystemRedesign()

  const updateTargetsCache = (nextTargets: ScheduledPostTarget[], baseFile: AlmanacFileShowItem = almanacFile) => {
    const updatedFile: AlmanacFileShowItem = {
      ...baseFile,
      scheduled_post_targets: nextTargets,
    }

    // Parent's handleUpdateAlmanacFile will update all caches (including calendar)
    onUpdateAlmanacFile?.(updatedFile)
  }

  const createAndPublishMutation = useMutation(
    async (variables: { scheduled_post_id: number; provider: ExternalIntegrationEnum; target_overrides: any }) => {
      return createScheduledPostTarget(variables, workspaceId)
    },
    {
      onSuccess: (newTargets) => {
        publishScheduledPostTargets({ scheduledPostId, targetIds: newTargets.map(({ id }) => id) })
        const nextTargets = uniqBy([...targets, ...newTargets], 'external_integration_id')
        updateTargetsCache(nextTargets)
      },
    },
  )

  useEffect(() => {
    document.body.classList.add('scheduled-post-targets-preview-visible')

    return () => {
      document.body.classList.remove('scheduled-post-targets-preview-visible')
    }
  }, [])

  const getTargetForPlatform = (platform: ExternalIntegrationEnum): ScheduledPostTarget | undefined => {
    return targets.find((target) => target.provider === platform)
  }

  const getPlatformNeedsReauth = (platform: ExternalIntegrationEnum): boolean => {
    return disconnectedIntegrations.some((integration) => integration.provider === platform)
  }

  const getAccountIdsNeedReauth = (platform: ExternalIntegrationEnum): number[] => {
    return disconnectedIntegrations
      .filter((integration) => integration.provider === platform)
      .map((integration) => integration.id)
  }

  const handleToggle = (platform: ExternalIntegrationEnum, isChecked: boolean) => {
    if (isChecked) {
      const optimisticTarget: ScheduledPostTarget = {
        id: -1,
        status: 'pending',
        target_overrides: {},
        scheduled_post_id: scheduledPostId,
        external_integration_id: 0,
        provider: platform,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      const previousTargets = targets.slice()
      const nextTargets = uniqBy([...targets, optimisticTarget], 'provider')
      updateTargetsCache(nextTargets)

      createMutation.mutate(
        {
          scheduled_post_id: scheduledPostId,
          provider: platform,
          target_overrides: {},
        },
        {
          // The toggle flow explicitly expects a single target to be created
          onSuccess: (newTargets) => {
            const newTarget = newTargets[0]
            if (!newTarget) return

            const resolvedTargets = nextTargets.map((target) =>
              target.id === optimisticTarget.id && target.provider === platform ? newTarget : target,
            )
            updateTargetsCache(resolvedTargets)
          },
          onError: () => {
            updateTargetsCache(previousTargets)
          },
        },
      )
    } else {
      const target = getTargetForPlatform(platform)
      if (!target) return
      const previousTargets = targets.slice()
      const nextTargets = previousTargets.filter((t) => t.provider !== platform)
      updateTargetsCache(nextTargets)

      deleteMutation.mutate(
        {
          scheduled_post_id: scheduledPostId,
          target_id: target.id,
        },
        {
          onError: () => {
            updateTargetsCache(previousTargets)
          },
        },
      )
    }
  }

  const handleCreateAndPublish = (platform: ExternalIntegrationEnum) => {
    const optimisticTarget: ScheduledPostTarget = {
      id: -1,
      status: 'pending',
      target_overrides: {},
      scheduled_post_id: scheduledPostId,
      external_integration_id: 0,
      provider: platform,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }
    const previousTargets = targets.slice()
    const nextTargets = uniqBy([...targets, optimisticTarget], 'provider')
    updateTargetsCache(nextTargets)

    createAndPublishMutation.mutate(
      {
        scheduled_post_id: scheduledPostId,
        provider: platform,
        target_overrides: {},
      },
      {
        onSuccess: (newTargets) => {
          publishScheduledPostTargets({ scheduledPostId, targetIds: newTargets.map(({ id }) => id) })
          const newTarget = newTargets[0]
          if (!newTarget) return
          const resolvedTargets = nextTargets.map((target) =>
            target.id === optimisticTarget.id && target.provider === platform ? newTarget : target,
          )
          updateTargetsCache(resolvedTargets)
        },
        onError: () => {
          updateTargetsCache(previousTargets)
        },
      },
    )
  }

  const isLastCheckedItem = (platform: ExternalIntegrationEnum): boolean => {
    const target = getTargetForPlatform(platform)
    const hasTarget = !!target

    // If status is AutoPostScheduled and this is the last checked item, disable the toggle
    if (status === PostStatusPropertyValue.AutoPostScheduled && hasTarget) {
      const checkedTargets = targets.filter((t) => !!t)
      return checkedTargets.length === 1
    }

    return false
  }

  const validatePost = (platform: ExternalIntegrationEnum) => {
    const target = getTargetForPlatform(platform)
    return validateScheduledPost(scheduledPost, platform, target)
  }

  const { isMobile } = useResponsiveDesign()
  if (isMobile) {
    return (
      <>
        {crossPostGroup.map((platform) => {
          const isAvailable = availablePlatforms?.includes(platform) || false
          const validation = validatePost(platform)
          const needsReauth = getPlatformNeedsReauth(platform)
          const accountIdsNeedReauth = getAccountIdsNeedReauth(platform)

          return (
            <ScheduledPostTargetPreviewItem
              almanacFile={almanacFile}
              key={platform}
              platform={platform}
              isLoading={false}
              isAvailable={isAvailable}
              onToggle={handleToggle}
              onCreateAndPublish={handleCreateAndPublish}
              isActive={activePlatform === platform}
              onPress={() => onSelectPlatform(platform)}
              isDisabled={isLastCheckedItem(platform)}
              isLocked={workspace?.is_ap_freemium && activePlatform !== platform}
              isAlreadyPosted={status === PostStatusPropertyValue.Posted}
              validation={validation}
              needsReauth={needsReauth}
              accountIdsNeedReauth={accountIdsNeedReauth}
            />
          )
        })}
      </>
    )
  }

  let title = approvalsEnabled ? 'Crosspost' : 'Posts'
  if (isStoryChannel(almanacFile.channel as DesignChannel)) {
    title = 'Post'
  }

  return (
    <ToolbarMenu
      title={title}
      data-testid="scheduled-post-targets-preview"
      height={isSystemRedesignEnabled ? 20 : undefined}
      action={
        approvalsEnabled ? undefined : (
          <Button
            variant="editor"
            size="sm"
            frontIcon={Settings}
            isDisabled={workspace?.is_ap_freemium}
            onPress={() => {
              openModal(
                ManageScheduledPostTargetsModal,
                { almanacFile, crossPostGroup, filters, onUpdateAlmanacFile, showVideoCrossPost },
                { newStack: true },
              )
            }}
          >
            Manage
          </Button>
        )
      }
      inDesigner={inDesigner}
    >
      <div className={Styles.content}>
        <div className={Styles.platforms}>
          {crossPostGroup.map((platform) => {
            const isAvailable = availablePlatforms?.includes(platform) || false
            const validation = validatePost(platform)
            const needsReauth = getPlatformNeedsReauth(platform)
            const accountIdsNeedReauth = getAccountIdsNeedReauth(platform)

            return (
              <ScheduledPostTargetPreviewItem
                key={platform}
                platform={platform}
                almanacFile={almanacFile}
                isLoading={false}
                isAvailable={isAvailable}
                onToggle={handleToggle}
                onCreateAndPublish={handleCreateAndPublish}
                isActive={activePlatform === platform}
                onPress={() => onSelectPlatform(platform)}
                isDisabled={isLastCheckedItem(platform)}
                isLastCheckedItem={isLastCheckedItem(platform)}
                isLocked={workspace?.is_ap_freemium && activePlatform !== platform}
                isAlreadyPosted={status === PostStatusPropertyValue.Posted}
                validation={validation}
                needsReauth={needsReauth}
                accountIdsNeedReauth={accountIdsNeedReauth}
              />
            )
          })}
        </div>
      </div>
    </ToolbarMenu>
  )
}
